import { useEffect, useState } from 'react';
import { api, type Me, type Role } from './api';

/**
 * 내 권한. 화면이 버튼을 보일지 정하는 데만 쓴다 — **강제는 서버가 한다.**
 * 여기서 숨긴다고 막히는 게 아니고, 서버 게이트(`requireRole`)가 진짜 방어선이다.
 *
 * 로그인 세션당 한 번만 부른다. 관리자가 권한을 바꾸면 다시 로그인해야 반영된다
 * (매 화면 갱신하면 목록 요청마다 왕복이 하나씩 더 붙는다).
 */
let cached: Promise<Me> | null = null;

export function loadMe(): Promise<Me> {
  if (!cached) cached = api.getMe();
  return cached;
}

/** 로그아웃 시 비운다 — 다음 사람이 앞사람 권한을 물고 시작하지 않게. */
export function clearMe(): void {
  cached = null;
}

export function useMe(): { me: Me | null; loading: boolean } {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    loadMe()
      .then((m) => {
        if (!cancelled) setMe(m);
      })
      .catch(() => {
        if (!cancelled) setMe(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { me, loading };
}

/**
 * 역할 포함 관계 — 서버 `functions/src/shared.ts` 의 `ROLE_IMPLIES` 와 **같은 표**다(한쪽만 고치면 버튼과 서버가 갈린다).
 * `accounting` ⊇ `settle`: 회계팀이 역할을 바꿔도 재무추가·할부금 쓰기를 잃지 않게. 반대는 아니다.
 */
const ROLE_IMPLIES: Partial<Record<Role, Role[]>> = { accounting: ['settle'] };

export const can = (role: Role | undefined, ...allowed: Role[]) =>
  role === 'admin' ||
  (!!role && (allowed.includes(role) || (ROLE_IMPLIES[role] ?? []).some((r) => allowed.includes(r))));

export const ROLE_LABEL: Record<Role, string> = {
  admin: '관리자',
  dispatch: '접수·배차',
  settle: '정산',
  accounting: '회계',
  view: '조회',
};

/**
 * 화면별 권한(2026-09-27 — 역할 + 팀·개인 예외). 판정은 **서버가 한 값**(`getMe.screens`)을 그대로 쓴다 —
 * 여기서 역할 표를 다시 짜면 화면과 서버가 갈린다. 강제는 서버 `requireScreen` 이 하고, 이건 메뉴·버튼용이다.
 * 아직 받기 전(me 없음)이면 아무것도 허락하지 않는다. 옛 응답(screens 없음)이면 역할 기본값으로 — 서버 roleDefault 와 같은 표.
 */
export type Screen = 'master' | 'dispatch' | 'settle' | 'result-daily' | 'result-mo' | 'result-req' | 'fin-inst' | 'accounting' | 'notices' | 'profit';
export type ScreenLevel = 'none' | 'read' | 'write';
const RANK: Record<ScreenLevel, number> = { none: 0, read: 1, write: 2 };

function roleFallback(role: Role, screen: Screen): ScreenLevel {
  if (screen === 'accounting') return role === 'accounting' ? 'write' : 'none';
  if (screen === 'notices') return role === 'dispatch' ? 'write' : 'none';
  if (screen === 'profit') return 'none';
  if (screen === 'master' || screen === 'dispatch') return role === 'dispatch' ? 'write' : 'read';
  return can(role, 'settle') ? 'write' : 'read';
}

export function screenLevel(me: Me | null | undefined, screen: Screen): ScreenLevel {
  if (!me) return 'none';
  if (me.role === 'admin') return 'write';
  return me.screens?.[screen] ?? roleFallback(me.role, screen);
}

/** 그 화면을 `need` 이상으로 쓸 수 있나. `screens` 를 여럿 주면 하나라도 되면 참. */
export function canScreen(me: Me | null | undefined, screens: Screen | Screen[], need: 'read' | 'write' = 'write'): boolean {
  return (Array.isArray(screens) ? screens : [screens]).some((s) => RANK[screenLevel(me, s)] >= RANK[need]);
}

export const SCREEN_LABEL: Record<Screen, string> = {
  master: '기준정보', dispatch: '접수·배차', settle: '정산', 'result-daily': '재무추가', 'result-mo': '월정기',
  'result-req': '영업정산', 'fin-inst': '할부금', accounting: '회계', notices: '공지사항', profit: '경영 분석',
};
